import type { PixelModule } from '@/runtime/types';
import type { PixelRuntimeContext } from '@/runtime/PixelRuntimeContext';
import type {
  AddPaymentInfoData,
  AddToCartData,
  InitiateCheckoutData,
  LeadData,
  PurchaseData,
  RemoveFromCartData,
  SearchData,
  ViewContentData,
} from '@/events/conversion';

import { ConversionEngine } from './conversion.engine';

export class ConversionModule implements PixelModule {
  readonly name = 'conversion' as const;

  private engine: ConversionEngine | null = null;

  private active = false;

  init(context: PixelRuntimeContext): void {
    if (this.active) {
      context.logger.debug('[Pixel] Conversion module already initialized');
      return;
    }

    if (!context.config.modules.conversion.enabled) {
      context.logger.debug('[Pixel] Conversion module disabled');
      return;
    }

    context.logger.debug('[Pixel] Conversion module initialized');

    this.engine = new ConversionEngine(context);
    this.engine.init();
    this.active = true;
  }

  destroy(): void {
    if (!this.active) return;

    this.engine?.destroy();
    this.engine = null;
    this.active = false;
  }

  purchase(data?: PurchaseData): void {
    this.engine?.factory.purchase.track(data);
  }

  lead(data: LeadData): void {
    this.engine?.factory.lead.track(data);
  }

  addToCart(data?: AddToCartData): void {
    this.engine?.factory.addToCart.track(data);
  }

  removeFromCart(data?: RemoveFromCartData): void {
    this.engine?.factory.removeFromCart.track(data);
  }

  initiateCheckout(data?: InitiateCheckoutData): void {
    this.engine?.factory.initiateCheckout.track(data);
  }

  addPaymentInfo(data?: AddPaymentInfoData): void {
    this.engine?.factory.addPaymentInfo.track(data);
  }

  viewContent(data?: ViewContentData): void {
    this.engine?.factory.viewContent.track(data);
  }

  search(data: SearchData): void {
    this.engine?.factory.search.track(data);
  }

  /** @internal For playground and development only. */
  getDebugDedupeKeys(): readonly string[] {
    return this.engine?.getDedupeKeys() ?? [];
  }

  /** @internal For playground and development only. */
  getDebugActiveDetectors(): readonly string[] {
    return this.engine?.getActiveDetectors() ?? [];
  }

  /** @internal For playground and development only. */
  isEngineActive(): boolean {
    return this.engine?.isActive() ?? false;
  }
}
